import { useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import { useLocale } from '../../i18n';

export interface MeritsViewProps {
  /** The player whose merits (top placements) are computed. */
  playerId: any;
  /** Tournament history rows ({ tournament, matches }) the merits are derived from. */
  history: any[];
  /**
   * Opens an archived (or seriespill) tournament. Optional — non-openable merits
   * render as disabled cards.
   */
  onOpenArchived?: (tournament: any) => void;
  /**
   * Resolves a player's final placement in a tournament ({ position, total, format, ... }).
   * deviation: originally the module-level `calcPlayerPositionInTournament`
   * helper, which is not yet extracted to src/engine and calls other helpers from
   * the original artifact; it is threaded in as a prop and the call-site forwards its own copy.
   */
  calcPlayerPositionInTournament: (playerId: any, tournament: any) => any;
}

/**
 * Player merits tab — trophy summary (per-format counts + gold) and the top-5
 * placement cards. Extracted verbatim from the original artifact (legacy/App.jsx);
 * markup/logic unchanged. Leaf component — reads no engine object.
 */
export function MeritsView({
  playerId,
  history,
  onOpenArchived,
  calcPlayerPositionInTournament,
}: MeritsViewProps) {
  // Named tr, not t: in the merit cards below, t is the tournament.
  const { t: tr, formatDate } = useLocale();
  // Compute placements for all finished tournaments
  const merits = useMemo(() => {
    // Size-aware merit rule: 1st place always counts. Top 3 counts only
    // when the group is large enough (placement ≤ half the field) — 3rd of 4 teams is closer
    // to next-to-last, not a merit. 4th/5th place counts only in large fields (≥ 8 teams).
    // Without a known field size: top 3.
    const qualifies = (pos, total) => {
      if (pos === 1) return true;
      if (!total || total < 2) return pos <= 3;
      if (pos <= 3 && pos <= total / 2) return true;
      if (pos <= 5 && total >= 8) return true;
      return false;
    };
    const list = [];
    history.forEach((h) => {
      const result = calcPlayerPositionInTournament(playerId, h.tournament);
      if (!result) return;
      if (!qualifies(result.position, result.total)) return;
      // Tournament date: use archivedAt or the latest match time
      const dateMs =
        h.tournament.archivedAt ||
        Math.max(0, ...(h.matches || []).map((m) => m.playedAt || m.completedAt || 0));
      list.push({ tournament: h.tournament, ...result, dateMs });
    });
    // Sort: lowest placement first (1st place on top). On equal placement, rank
    // by tournament type: Klubbmesterskap and Seriespill first (extra prominent),
    // then Padel24, finally Americano. Lastly the newest.
    const fmtRank = (x) =>
      x.format === 'klubbmesterskap'
        ? 0
        : x.format === 'seriespill'
          ? 1
          : x.format === 'americano'
            ? 3
            : 2;
    list.sort((a, b) => {
      if (a.position !== b.position) return a.position - b.position;
      const fr = fmtRank(a) - fmtRank(b);
      if (fr !== 0) return fr;
      return b.dateMs - a.dateMs;
    });
    return list;
    // deviation: the original dep array intentionally omits the stable
    // `calcPlayerPositionInTournament` function prop; preserved verbatim.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId, history]);

  if (merits.length === 0) {
    return (
      <div className="card p-8 text-center text-sm text-ink-3 italic">
        {tr('players.merits.empty')}
      </div>
    );
  }

  // Format-based trophy summary: KM, Padel24 and Seriespill = large trophies (🏆),
  // Americano = medal (🏅). Counts the number of merits per format (+ gold separately).
  const fmtKey = (m) =>
    m.format === 'klubbmesterskap'
      ? 'klubbmesterskap'
      : m.format === 'seriespill'
        ? 'seriespill'
        : m.format === 'americano'
          ? 'americano'
          : 'padel24';
  const fmtCounts = { klubbmesterskap: 0, seriespill: 0, padel24: 0, americano: 0 };
  const fmtGold = { klubbmesterskap: 0, seriespill: 0, padel24: 0, americano: 0 };
  merits.forEach((m) => {
    const k = fmtKey(m);
    fmtCounts[k]++;
    if (m.position === 1) fmtGold[k]++;
  });
  const fmtSummary = [
    { k: 'klubbmesterskap', label: 'Klubbmesterskap', icon: '🏆', big: true },
    { k: 'seriespill', label: 'Seriespill', icon: '🏆', big: true },
    { k: 'padel24', label: 'Padel24', icon: '🏆', big: true },
    { k: 'americano', label: 'Americano', icon: '🏅', big: false },
  ];
  // Icon per card (position medal): 1-3 medals, otherwise trophy.
  const iconFor = (pos) => (pos === 1 ? '🥇' : pos === 2 ? '🥈' : pos === 3 ? '🥉' : '🏆');
  // deviation: `formatLabel` was dead in the original artifact too (never referenced in the
  // JSX below) — kept verbatim for byte-fidelity; eslint-disabled rather than removed.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const formatLabel = (m) =>
    m.format === 'americano'
      ? m.type === 'standings'
        ? 'Americano (totalstanding)'
        : 'Americano (finale)'
      : m.format === 'klubbmesterskap'
        ? // Concatenated rather than interpolated so the format NAME stays a bare
          // literal: it is protocol vocabulary, not a translatable string.
          'Klubbmesterskap' + (m.className ? ' · ' + m.className : '')
        : m.format === 'seriespill'
          ? `Seriespill${m.className ? ' · ' + m.className : ''}${m.season ? ' · Sesong ' + m.season : ''}`
          : 'Padel24';

  return (
    <div className="space-y-4">
      {/* Trophy summary (format-based) */}
      <div className="card p-4">
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-2">
          {tr('players.merits.trophies')}
        </div>
        <div className="flex flex-wrap gap-5 items-end">
          {fmtSummary
            .filter((f) => fmtCounts[f.k] > 0)
            .map((f) => (
              <div key={f.k} className="flex items-center gap-2">
                <span className="leading-none" style={{ fontSize: f.big ? 34 : 22 }}>
                  {f.icon}
                </span>
                <div>
                  <div className="font-display text-xl font-bold leading-none">
                    {fmtCounts[f.k]}
                  </div>
                  <div className="font-mono text-[10px] text-ink-3 uppercase tracking-wider">
                    {f.label}
                  </div>
                  {fmtGold[f.k] > 0 && (
                    <div className="font-mono text-[10px] text-ink-3 mt-0.5">🥇 {fmtGold[f.k]}</div>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Top 5 merits — large cards, sorted by placement and type */}
      <div className="space-y-2">
        {merits.map((m, i) => {
          const t = m.tournament;
          const date = m.dateMs ? new Date(m.dateMs) : null;
          const dateLabel = date
            ? formatDate(date, { day: 'numeric', month: 'short', year: 'numeric' })
            : '';
          const bg =
            m.position === 1
              ? 'bg-lime-bg/30'
              : m.position === 2
                ? 'bg-bg-soft/60'
                : m.position === 3
                  ? 'bg-amber-50/60'
                  : 'bg-paper';
          // Type prominence: Klubbmesterskap and Seriespill most prominent, then Padel24, then Americano.
          const fmt =
            m.format === 'klubbmesterskap'
              ? 'km'
              : m.format === 'seriespill'
                ? 'serie'
                : m.format === 'americano'
                  ? 'amer'
                  : 'p24';
          const big = fmt === 'km' || fmt === 'serie';
          const cardClass = fmt === 'amer' ? 'card' : 'card-elev';
          const pad = big ? 'p-4' : fmt === 'p24' ? 'p-3.5' : 'p-3';
          const iconSize = big ? 'text-4xl' : fmt === 'p24' ? 'text-3xl' : 'text-2xl';
          const nameSize = big ? 'text-xl' : fmt === 'p24' ? 'text-lg' : 'text-sm';
          const accent = fmt === 'km' ? '#c2a14a' : fmt === 'serie' ? '#3b6fb5' : null; // gold / blue prestige
          const badge =
            fmt === 'km' ? (
              <span
                className="font-mono text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ background: accent, color: '#fff' }}
              >
                Klubbmesterskap{m.className ? ' · ' + m.className : ''}
              </span>
            ) : fmt === 'serie' ? (
              <span
                className="font-mono text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ background: accent, color: '#fff' }}
              >
                Seriespill{m.className ? ' · ' + m.className : ''}
                {m.season ? ' · S' + m.season : ''}
              </span>
            ) : fmt === 'p24' ? (
              <span
                className="font-mono text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border"
                style={{ borderColor: 'rgba(15,22,17,0.25)', color: '#4a5a50' }}
              >
                Padel24
              </span>
            ) : (
              <span className="font-mono text-[9px] uppercase tracking-wider text-ink-3">
                {m.type === 'standings' ? 'Americano · totalstanding' : 'Americano'}
              </span>
            );
          const canOpen = t.isArchived || t.type === 'seriespill';
          return (
            <button
              key={t.archiveId || t.id || i}
              onClick={() => canOpen && onOpenArchived?.(t)}
              disabled={!canOpen}
              className={`${cardClass} ${pad} text-left w-full transition-all ${bg} ${canOpen ? 'cursor-pointer hover:shadow-md' : 'cursor-default'} ${fmt === 'amer' ? 'opacity-90' : ''}`}
              style={big ? { borderLeft: `4px solid ${accent}` } : undefined}
            >
              <div className="flex items-center gap-3">
                <span className={`${iconSize} flex-shrink-0`}>{iconFor(m.position)}</span>
                <div className="flex-1 min-w-0">
                  <div className={`font-display ${nameSize} font-semibold truncate leading-tight`}>
                    {t.config?.name || tr('players.merits.tournamentFallback')}
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {badge}
                    <span className="font-mono text-[10px] text-ink-3 uppercase tracking-wider">
                      {tr('players.merits.placementDate', {
                        position: m.position,
                        date: dateLabel,
                      })}
                    </span>
                  </div>
                </div>
                {canOpen && <ChevronRight size={18} className="text-ink-3 flex-shrink-0" />}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
